import { Body, Icon, Tooltip } from '@assembly-js/design-system';
import { Divider, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormikContext } from 'formik';
import { isString } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { BackIcon, TrashIcon } from 'src/components/icons';
import {
  BANNER_HEIGHT,
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
} from 'src/constants/uiConsts';
import MenuLogo from 'src/features/drawer/MenuLogo';
import { useDrawerWidth } from 'src/features/drawer/useDrawerWidth';
import Activator from 'src/legacy/components/Activator';
import Button from 'src/legacy/components/Button';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { setInvoicePreviewStatusAction } from 'src/store/payments/actions';
import { RootState } from 'src/store/reduxTypes';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';
import * as Colors from 'src/theme/colors';

import { FormPageHeaderProps } from '../formPageTypes';

const useStyles = makeStyles<{
  drawerWidth: number;
  isSettingsForm: boolean;
}>()((theme, props) => ({
  pageHeader: {
    height: DESKTOP_APP_BAR_HEIGHT,
    position: 'absolute',
    border: 'unset',
    zIndex: theme.zIndex.drawer + 2,
    borderRadius: 0,
    padding: theme.spacing(1, 4.5),
    left: props.drawerWidth,
    width: `calc(100% - ${props.drawerWidth}px)`,
    [theme.breakpoints.down('sm')]: {
      left: 0,
      width: `100%`,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 2),
      height: MOBILE_APP_BAR_HEIGHT,
    },
    boxSizing: 'border-box',
  },
  titleText: {
    color: Colors.BlackHeadings,
    whiteSpace: 'nowrap',
  },
  unsavedText: {
    color: theme.palette.primary.main,
  },
  confirmButton: {
    marginLeft: theme.spacing(2),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(1),
    },
    '& button': {
      whiteSpace: 'nowrap',
    },
  },
  discardButton: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
    },
    '& button': {
      whiteSpace: 'nowrap',
    },
  },
  deleteButtonContainer: {
    marginLeft: theme.spacing(1),
  },
  deleteButton: {
    color: Colors.red,
  },
  previewSeparator: {
    margin: theme.spacing(0, 2),
    height: 22,
  },
  backIconContainer: {
    fontSize: 20,
    paddingRight: theme.spacing(3),
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    overflow: 'hidden',
    paddingRight: theme.spacing(2),
  },
}));

const FormPageHeaderBase: React.FC<FormPageHeaderProps> = ({
  onCancel,
  isSubmitting,
  cancelLabel,
  actionLabel,
  title,
  isNew,
  className,
  handleSubmit,
  position = 'top',
  showActions = true,
  isSettingsForm,
  isDisabled,
  tooltipComponent,
  showDeleteActon = false,
  hideCancelAction = false,
  deleteLabel,
  onDelete,
  showBackButton = false,
  onBackButtonClick,
  cancelDisabled,
}) => {
  const drawerWidth = useDrawerWidth();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const formikContext = !isMobileView ? useFormikContext() : null;

  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );

  const { classes, cx } = useStyles({
    isSettingsForm: isSettingsForm ?? false,
    drawerWidth,
  });

  /**
   * This method it used to determine the form
   * action bar vertical position (top/bottom)
   * depending on where it is displayed.
   * e.g. in settings modal:
   * it should be on the top on mobile device
   * it should be on the bottom on desktop
   */
  const getFormActionPositionStyle = (): React.CSSProperties => {
    if (position === 'bottom') {
      return { bottom: 0 };
    }

    return {
      top: bannerOptions ? BANNER_HEIGHT : 0,
      position: 'fixed',
    };
  };
  /**
   * This method is used to determine the formPageActionBar
   * content positioning.
   * e.g. when a form page have a title, we should display
   * content spaced-between
   */
  const getFormPageActionJustifyContent = () => {
    if (title || isMobileView) return 'space-between';
    return 'flex-end';
  };
  const canPreviewInvoice = useSelector(
    (state: RootState) => state.payments.canPreviewInvoice,
  );
  const invoicePreviewEnabled = useSelector(
    (state: RootState) => state.payments.invoicePreviewEnabled,
  );

  const toggleInvoicePreviewStatus = () => {
    dispatch(setInvoicePreviewStatusAction(!invoicePreviewEnabled));
  };

  return (
    <Paper
      variant="outlined"
      className={cx(classes.pageHeader, className)}
      style={{ ...getFormActionPositionStyle() }}
    >
      <div
        className="flex items-center h-full"
        style={{ justifyContent: getFormPageActionJustifyContent() }}
      >
        <div className={classes.titleWrapper}>
          {showBackButton ? (
            <>
              {isMobileView ? (
                <MenuLogo
                  menuClickCallback={onBackButtonClick}
                  Icon={<Icon icon="ChevronLeft" />}
                />
              ) : (
                <BackIcon
                  className={classes.backIconContainer}
                  onClick={onBackButtonClick}
                />
              )}
            </>
          ) : (
            <MenuLogo
              menuClickCallback={() =>
                dispatch(togglePrimarySidebarMobile({ isOpen: true }))
              }
            />
          )}
          {isString(title) ? (
            <BaseTypography fontType="18Medium" className={classes.titleText}>
              {title}
            </BaseTypography>
          ) : (
            title
          )}
        </div>
        {showActions && (
          <div className="flex">
            {canPreviewInvoice &&
              !isMobileView &&
              !isSettingsForm &&
              formikContext && (
                <>
                  <div className="pt-1">
                    <Activator
                      checked={invoicePreviewEnabled}
                      label="Preview"
                      onChange={toggleInvoicePreviewStatus}
                      onBlur={formikContext.handleBlur}
                      isFixedWidth={false}
                    />
                  </div>
                  <div className="mx-2 pt-1">
                    <Divider
                      orientation="vertical"
                      className={classes.previewSeparator}
                    />
                  </div>
                </>
              )}
            {!hideCancelAction && (
              <div className={classes.discardButton}>
                <Button
                  htmlId="cancelFormButton"
                  variant="contained"
                  color="secondary"
                  onClick={onCancel}
                  disabled={cancelDisabled}
                >
                  {cancelLabel}
                </Button>
              </div>
            )}
            {showDeleteActon && (
              <div className={classes.deleteButtonContainer}>
                <Button
                  htmlId="deleteButton"
                  variant="contained"
                  color="secondary"
                  onClick={onDelete}
                  startIcon={<TrashIcon />}
                  className={classes.deleteButton}
                >
                  {deleteLabel}
                </Button>
              </div>
            )}
            <Tooltip
              content={
                tooltipComponent ? (
                  <Body size="sm" className="max-w-[288px]">
                    {tooltipComponent}
                  </Body>
                ) : (
                  ''
                )
              }
              position="bottom"
            >
              <div className={classes.confirmButton}>
                <Button
                  data-testid="submitFormButton"
                  htmlId="submitFormButton"
                  variant="contained"
                  type="submit"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  disabled={isDisabled}
                  color="primary"
                >
                  {actionLabel || (isNew ? 'Create' : 'Update')}
                </Button>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
      <div />
    </Paper>
  );
};

export default FormPageHeaderBase;
